// R-52 sign-in screens, Google only. Entry points: the Saved card after the first save (SavedSignInCard), the line after
// sharing a day (ShareSignInNote), the Settings row (SignInRow); the line's Sign in opens the sign-in sheet. One host
// mounted in App shows that sheet and what happens when Google sends someone back. Never blocks anything, with one
// exception: back from Google the app is held until the merge lands (or the person lets it go with Not now), because
// nothing may run as the new account before it. Otherwise every state has Not now / Done and swipe-down closes it.
import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { f2 } from '../copyFlow2'
import { t } from '../copy'
import { updatePlan, placeOf, planTitle, type Plan } from '../lib/plan'
import {
  closeSignIn, dismissMergeFailed, inAppBrowser, openSignIn, reopenSignIn, resumeSignIn, retryMerge, showSavedCard, dismissSavedCard, startGoogle, useSignInGate, useSignInStatus, useWho, type MovedPlan, type SignInFrom,
} from '../lib/signin'
import { Sheet } from '../ui/Sheet'
import { Button } from '../ui/Button'
import { Spinner } from '../ui/Spinner'

const onlineSub = (cb: () => void) => {
  window.addEventListener('online', cb)
  window.addEventListener('offline', cb)
  return () => {
    window.removeEventListener('online', cb)
    window.removeEventListener('offline', cb)
  }
}
const useOnline = () => useSyncExternalStore(onlineSub, () => navigator.onLine, () => true)

/**
 * The sign-in choices. Providers are a list so another way in (an email link, once there is a sending domain) is one
 * more entry, not a new layout.
 */
function Choices({ via }: { via: SignInFrom }) {
  const online = useOnline()
  const blocked = inAppBrowser()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(false)
  const providers = [{ key: 'google', label: f2('signin.google'), go: () => startGoogle(via) }]
  return (
    <div className="mt-5 flex flex-col gap-2">
      {providers.map((p) => (
        <Button key={p.key} block disabled={!online || blocked || busy} data-review-id={`signin-${p.key}`}
          onClick={async () => {
            setBusy(true)
            setError(false)
            try {
              if (!(await p.go())) setBusy(false) // true = leaving the page for Google
            } catch (e) {
              Sentry.captureException(e)
              setError(true)
              setBusy(false)
            }
          }}>
          {busy ? <Spinner size={20} label={false} className="text-current" /> : <GoogleMark />}
          {p.label}
        </Button>
      ))}
      <div aria-live="polite">
        {!online && <p className="t-meta text-ink-2">{f2('signin.offline')}</p>}
        {online && blocked && <p className="t-meta text-ink-2">{f2('signin.in_app_browser')}</p>}
        {error && <p role="alert" className="t-meta font-medium text-danger">{t('error.generic')}</p>}
      </div>
    </div>
  )
}

function GoogleMark() {
  return (
    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 48 48" className="shrink-0 rounded-full bg-white p-[1px]">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  )
}

/** Inline sign-in (the Settings row, the Saved card): the same choices as the sheet, in the page. */
export function SignInRow({ via, title, sub }: { via: SignInFrom; title?: string; sub: string }) {
  return (
    <div data-review-id={`signin-row-${via}`}>
      {title && <h2 className="t-headline">{title}</h2>}
      <p className={`${title ? 'mt-1.5 text-[15px]' : 't-meta'} text-ink-2`}>{sub}</p>
      <Choices via={via} />
      <p className="t-caption mt-1 text-ink-2">{f2('signin.privacy')}</p>
    </div>
  )
}

/** E2 on the Saved screen, after the first save: an in-page card (never a sheet: it would swallow the next tap). */
export function SavedSignInCard() {
  const who = useWho()
  const [gone, setGone] = useState(false)
  // the first save sets the flag; re-read on it, whatever re-renders the Saved list
  const [, setTick] = useState(0)
  useEffect(() => {
    const on = () => setTick((n) => n + 1)
    window.addEventListener('mg:add', on)
    return () => window.removeEventListener('mg:add', on)
  }, [])
  if (gone || !who.ready || !showSavedCard()) return null
  return (
    <section data-review-id="signin-saved-card" className="mt-8 border border-line bg-surface p-4">
      <SignInRow via="e2" title={f2('signin.prompt_title')} sub={f2('signin.prompt_body')} />
      <Button variant="ghost" block className="mt-1" onClick={() => { dismissSavedCard(); setGone(true) }}>{f2('signin.later')}</Button>
    </section>
  )
}

/** E3 on the plan page: after a share link is made, a quiet line that the plan lives only here until they sign in. */
export function ShareSignInNote() {
  const who = useWho()
  if (!who.ready || !who.anonymous) return null
  return (
    <div data-review-id="signin-share-note" className="mt-2">
      <p className="t-meta text-ink-2">{f2('signin.share_line')}</p>
      <button type="button" onClick={() => openSignIn('share')} className="press t-meta -ml-1 inline-flex min-h-11 items-center px-1 font-semibold text-ink underline underline-offset-4">
        {f2('signin.card_button')}
      </button>
    </div>
  )
}

/** Like the plan list: its own name, else the place (and dates when it has them). planTitle needs dates. */
function titleOf(p: MovedPlan): string {
  if (p.title?.trim()) return p.title.trim()
  if (p.start_date && p.end_date) return planTitle(p as unknown as Plan)
  return placeOf({ dest: p.dest ?? null } as unknown as Plan)
}

function MergedList({ plans }: { plans: MovedPlan[] }) {
  const [removed, setRemoved] = useState<Record<string, 'busy' | 'done' | 'error'>>({})
  const remove = async (id: string) => {
    setRemoved((r) => ({ ...r, [id]: 'busy' }))
    try {
      await updatePlan(id, { status: 'archived' })
      setRemoved((r) => ({ ...r, [id]: 'done' }))
    } catch (e) {
      Sentry.captureException(e)
      setRemoved((r) => ({ ...r, [id]: 'error' }))
    }
  }
  return (
    <section className="mt-4">
      <h3 className="t-meta font-semibold text-ink-2">{f2('signin.merged_from_phone')}</h3>
      <ul className="mt-1 divide-y divide-line">
        {plans.map((p) => (
          <li key={p.id} className="flex min-h-12 items-center justify-between gap-3">
            <span className="min-w-0 truncate text-[16px] text-ink">{titleOf(p)}</span>
            {removed[p.id] === 'done' ? (
              <span className="t-meta shrink-0 text-ink-2">{f2('signin.merged_removed')}</span>
            ) : (
              <button type="button" disabled={removed[p.id] === 'busy'} onClick={() => void remove(p.id)}
                className="press t-meta inline-flex min-h-11 shrink-0 items-center font-medium text-ink-2 underline underline-offset-4 disabled:opacity-40">
                {removed[p.id] === 'error' ? t('retry') : f2('signin.merged_remove')}
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

export function SignInHost() {
  const st = useSignInStatus()
  const gated = useSignInGate()
  const navigate = useNavigate()
  const started = useRef(false)
  useEffect(() => {
    if (started.current) return
    started.current = true
    void resumeSignIn((to) => navigate(to, { replace: true }))
  }, [navigate])

  const close = () => closeSignIn(st.kind === 'prompt' ? st.via : undefined)
  const open = st.kind !== 'idle'
  const label = st.kind === 'prompt' ? f2('signin.prompt_title') : st.kind === 'merged' ? f2('signin.merged_title') : f2('signin.opened_title')

  return (
    <Sheet open={open} onClose={close} label={label} locked={(st.kind === 'working' && !st.slow) || (gated && st.kind !== 'done' && st.kind !== 'merged')} reviewState="signin">
      {st.kind === 'prompt' && (
        <div data-review-id="signin-sheet">
          <h2 className="t-control pr-10 text-[19px] leading-6 text-ink">{f2('signin.prompt_title')}</h2>
          <p className="mt-1.5 text-[15px] text-ink-2">{f2('signin.prompt_body')}</p>
          <Choices via={st.via} />
          <p className="t-caption mt-1 text-ink-2">{f2('signin.privacy')}</p>
          <Button variant="ghost" block className="mt-2" onClick={close}>{f2('signin.later')}</Button>
        </div>
      )}
      {st.kind === 'working' && (
        <div data-review-id="signin-working" aria-live="polite">
          <p className="flex items-center gap-3 pr-10 text-[17px] text-ink"><Spinner size={20} label={false} />{f2('signin.working')}</p>
          {st.slow && (
            <>
              <p className="mt-3 text-[15px] text-ink-2">{f2('signin.slow_body')}</p>
              <div className="mt-4 flex flex-col gap-2">
                <Button block onClick={() => location.reload()}>{f2('signin.retry')}</Button>
                {/* while the app is held for the merge, closing would leave a blank screen: Try again only */}
                {!gated && <Button variant="ghost" block onClick={() => closeSignIn()}>{f2('signin.later')}</Button>}
              </div>
            </>
          )}
        </div>
      )}
      {st.kind === 'done' && (
        <div data-review-id="signin-done">
          <h2 className="t-control pr-10 text-[19px] leading-6 text-ink">{f2('signin.opened_title')}</h2>
          <p className="mt-1.5 text-[15px] text-ink-2">{f2('signin.opened_body')}</p>
          <Button block className="mt-5" onClick={() => closeSignIn()}>{f2('signin.merged_done')}</Button>
        </div>
      )}
      {st.kind === 'merged' && (
        <div data-review-id="signin-merged">
          <h2 className="t-control pr-10 text-[19px] leading-6 text-ink">{f2('signin.merged_title')}</h2>
          <p className="mt-1.5 text-[15px] text-ink-2">{f2('signin.merged_body')}</p>
          <MergedList plans={st.plans} />
          <Button block className="mt-5" onClick={() => closeSignIn()}>{f2('signin.merged_done')}</Button>
        </div>
      )}
      {(st.kind === 'failed' || st.kind === 'merge_failed') && (
        <div data-review-id={`signin-${st.kind}`}>
          {st.kind === 'failed' ? (
            <>
              <h2 className="t-control pr-10 text-[19px] leading-6 text-ink">{f2('signin.failed_title')}</h2>
              <p className="mt-1.5 text-[15px] text-ink-2">{f2('signin.failed_body')}</p>
            </>
          ) : (
            <p className="pr-10 text-[17px] leading-6 text-ink">{f2('signin.merge_failed')}</p>
          )}
          <div className="mt-5 flex flex-col gap-2">
            <Button block onClick={() => (st.kind === 'failed' ? reopenSignIn() : void retryMerge().catch((e) => { Sentry.captureException(e); closeSignIn() }))}>
              {f2('signin.retry')}
            </Button>
            <Button variant="ghost" block onClick={() => (st.kind === 'merge_failed' ? dismissMergeFailed() : closeSignIn())}>{f2('signin.later')}</Button>
          </div>
        </div>
      )}
    </Sheet>
  )
}
